/**
 * Exam type selector component.
 *
 * Displays exam type options (Goethe, ÖSD, telc) for selection. Shows availability
 * status based on selected level (ÖSD and telc are locked for A1).
 *
 * @module ExamTypeSelector
 */

import { GraduationCap, BookOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ExamType } from '@/types/exam-simulation';
import { useTranslation } from 'react-i18next';

interface ExamTypeOption {
  id: ExamType;
  icon: typeof GraduationCap;
  color: string;
  isAvailable: boolean;
}

interface ExamTypeSelectorProps {
  readonly onSelect: (examType: ExamType) => void;
}

const examTypes: ExamTypeOption[] = [
  {
    id: 'goethe',
    icon: GraduationCap,
    color: 'text-blue-500',
    isAvailable: true,
  },
  {
    id: 'osd',
    icon: BookOpen,
    color: 'text-red-500',
    isAvailable: false,
  },
  {
    id: 'telc',
    icon: GraduationCap,
    color: 'text-green-500',
    isAvailable: false,
  },
];

export function ExamTypeSelector({ onSelect }: ExamTypeSelectorProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6 sm:space-y-8">
      <div className="text-center px-4">
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-2">{t('examSelection.title')}</h2>
        <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">{t('examSelection.subtitle')}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {examTypes.map((exam) => {
          const Icon = exam.icon;
          return (
            <Card
              key={exam.id}
              role="button"
              tabIndex={0}
              aria-disabled={!exam.isAvailable}
              aria-label={`${t(`examSelection.types.${exam.id}.name`)}${!exam.isAvailable ? `, ${t('common.comingSoon', { defaultValue: 'Coming Soon' })}` : ''}`}
              className={cn(
                'relative transition-all duration-200 cursor-pointer',
                'hover:shadow-lg hover:border-primary/50',
                'flex flex-col items-center text-center p-6 sm:p-8',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                !exam.isAvailable && 'opacity-60 grayscale cursor-not-allowed border-muted',
              )}
              onClick={() => exam.isAvailable && onSelect(exam.id)}
              onKeyDown={(e) => {
                if (exam.isAvailable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(exam.id);
                }
              }}
            >
              {!exam.isAvailable && (
                <div className="absolute top-2 right-2">
                  <Badge variant="secondary" className="text-[10px] uppercase">{t('examSelection.comingSoon')}</Badge>
                </div>
              )}
              <CardContent className="flex flex-col items-center gap-3 sm:gap-4 w-full p-0">
                <div className={cn(
                  'w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center rounded-2xl bg-muted/50 transition-colors group-hover:bg-primary/5',
                  exam.color
                )}>
                  <Icon className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-base sm:text-lg text-foreground">{t(`examSelection.types.${exam.id}.name`)}</h3>
                  <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{t(`examSelection.types.${exam.id}.description`)}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
